import { useState } from "react";
import { ArrowRight, Check, Apple } from "lucide-react";
import { GooglePlayLogo } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import Reveal from "@/components/landing/Reveal";
import { useI18n } from "@/lib/i18n";

export default function FinalCTA() {
  const { t } = useI18n();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      const normalizedEmail = email.trim().toLowerCase();
      const waitlistKey = "healthbreak.waitlist";
      const currentList = JSON.parse(localStorage.getItem(waitlistKey) || "[]");
      const isAlreadyAdded = currentList.includes(normalizedEmail);

      if (!isAlreadyAdded) {
        currentList.push(normalizedEmail);
        localStorage.setItem(waitlistKey, JSON.stringify(currentList));
      }

      setDone(true);
      toast.success(t("cta.success"));
    } catch {
      toast.error(t("cta.toastError"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="download"
      data-testid="final-cta-section"
      className="relative py-24 sm:py-32 overflow-hidden"
    >
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute -top-24 left-1/2 -translate-x-1/2 h-[34rem] w-[60rem] rounded-full bg-emerald-200/60 dark:bg-emerald-700/20 blur-3xl" />
      </div>

      <div className="mx-auto max-w-4xl px-5 sm:px-8 text-center">
        <Reveal>
          <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-emerald-800 dark:text-emerald-400">
            {t("cta.kicker")}
          </div>
          <h2 className="mt-4 font-display font-bold text-4xl sm:text-6xl tracking-tight leading-[1.05] text-balance text-neutral-900 dark:text-neutral-50">
            {t("cta.h2Pre")}{" "}
            <span className="text-emerald-800 dark:text-emerald-400">
              {t("cta.h2Highlight")}
            </span>{" "}
            <br />
            {t("cta.h2Post")}
          </h2>
          <p className="mt-6 text-lg text-neutral-600 dark:text-neutral-300 leading-relaxed max-w-xl mx-auto">
            {t("cta.lead")}
          </p>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3">
            <a
              href="#"
              data-testid="apple-store-cta"
              aria-label="Download on the App Store"
              className="inline-flex items-center gap-3 h-14 px-6 rounded-2xl bg-neutral-950 text-stone-50 dark:bg-neutral-50 dark:text-neutral-950 hover:-translate-y-0.5 transition pill-shadow"
            >
              <Apple className="h-6 w-6" />
              <div className="text-left leading-tight">
                <div className="text-[10px] uppercase tracking-widest opacity-80">
                  {t("cta.appStoreSmall")}
                </div>
                <div className="font-display font-semibold text-base">
                  {t("cta.appStoreBig")}
                </div>
              </div>
            </a>
            <a
              href="#"
              data-testid="google-play-cta"
              aria-label="Get it on Google Play"
              className="inline-flex items-center gap-3 h-14 px-6 rounded-2xl bg-neutral-950 text-stone-50 dark:bg-neutral-50 dark:text-neutral-950 hover:-translate-y-0.5 transition pill-shadow"
            >
              <GooglePlayLogo size={22} weight="fill" />
              <div className="text-left leading-tight">
                <div className="text-[10px] uppercase tracking-widest opacity-80">
                  {t("cta.googleSmall")}
                </div>
                <div className="font-display font-semibold text-base">
                  {t("cta.googleBig")}
                </div>
              </div>
            </a>
          </div>
        </Reveal>

        <Reveal delay={0.18}>
          <div className="mt-12 max-w-xl mx-auto">
            <div className="text-xs uppercase tracking-[0.22em] font-bold text-neutral-500 mb-4">
              {t("cta.orWaitlist")}
            </div>
            {done ? (
              <div
                data-testid="waitlist-success"
                className="rounded-full border border-emerald-800/20 dark:border-emerald-400/30 bg-emerald-50 dark:bg-emerald-400/10 text-emerald-900 dark:text-emerald-200 px-6 py-4 inline-flex items-center gap-3"
              >
                <Check className="h-4 w-4" />
                <span className="text-sm font-medium">{t("cta.success")}</span>
              </div>
            ) : (
              <form
                data-testid="waitlist-form"
                onSubmit={onSubmit}
                className="flex flex-col sm:flex-row items-stretch gap-3"
              >
                <label htmlFor="waitlist-email" className="sr-only">
                  Email
                </label>
                <Input
                  id="waitlist-email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t("cta.placeholder")}
                  data-testid="waitlist-email-input"
                  className="h-14 rounded-full px-6 text-base bg-white dark:bg-neutral-900 border-neutral-300 dark:border-neutral-700 focus-visible:ring-emerald-700 focus-visible:ring-offset-0"
                />
                <Button
                  type="submit"
                  disabled={loading}
                  data-testid="waitlist-submit-button"
                  className="h-14 rounded-full px-7 text-base font-semibold bg-emerald-800 hover:bg-emerald-900 text-stone-50 hover:text-white pill-shadow hover:-translate-y-0.5 transition disabled:opacity-70"
                >
                  {loading ? t("cta.submitLoading") : (
                    <span className="inline-flex items-center gap-2">
                      {t("cta.submit")} <ArrowRight className="h-4 w-4" />
                    </span>
                  )}
                </Button>
              </form>
            )}
            <div className="mt-3 text-xs text-neutral-500">
              {t("cta.privacyNote")}
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
